// src/context/ProjectContext.js
import React, { useEffect, createContext, useContext, useState, useCallback, useRef } from 'react';
import { VerbatimProvider, useVerbatim } from './VerbatimContext';
import { SetupProvider, useSetup } from './SetupContext';
import { saveToLocalStorage, loadFromLocalStorage } from '../utils/StorageUtils';

const ProjectContext = createContext();

export const useProject = () => useContext(ProjectContext);

export const ProjectProvider = ({ children }) => {
  return (
    <VerbatimProvider>
      <SetupProvider>
        <ProjectProviderContent defaultEnableAutosave={true} defaultAutosaveInterval={15}>
          {children}
        </ProjectProviderContent>
      </SetupProvider>
    </VerbatimProvider>
  );
};

const ProjectProviderContent = ({ children, defaultEnableAutosave, defaultAutosaveInterval }) => {
  const { verbatims, notes, slides, setVerbatims, setNotes, setSlides } = useVerbatim();
  const { labels, labelTypes, setLabels, setLabelTypes } = useSetup();
  const [isLoading, setIsLoading] = useState(true);

  // État pour les paramètres d'autosauvegarde
  const [enableAutosave, setEnableAutosave] = useState(defaultEnableAutosave);
  const [autosaveInterval, setAutosaveInterval] = useState(defaultAutosaveInterval);

  const dataRef = useRef({
    verbatims: verbatims || [],
    notes: notes || [],
    slides: slides || [],
    labels: labels || {},
    labelTypes: labelTypes || {}
  });

  // Charger les paramètres d'autosauvegarde depuis IndexedDB
  useEffect(() => {
    const loadAutosaveSettings = async () => {
      try {
        // S'assurer que les paramètres par défaut sont initialisés
        const { initializeDefaultSettings } = await import('../utils/IndexedDBUtils');
        await initializeDefaultSettings();
        
        // Maintenant, charger les paramètres
        const savedEnableAutosave = await loadFromLocalStorage('enableAutosave');
        const savedAutosaveInterval = await loadFromLocalStorage('autosaveInterval');
        
        // Utiliser les valeurs de IndexedDB ou les défauts si non disponibles
        setEnableAutosave(savedEnableAutosave !== null && savedEnableAutosave !== undefined 
          ? savedEnableAutosave 
          : defaultEnableAutosave);
        
        setAutosaveInterval(savedAutosaveInterval !== null && savedAutosaveInterval !== undefined 
          ? savedAutosaveInterval 
          : defaultAutosaveInterval);
        
        setIsLoading(false);
      } catch (error) {
        console.error('Erreur lors du chargement des paramètres d\'autosauvegarde:', error);
        // En cas d'erreur, utiliser les valeurs par défaut
        setEnableAutosave(defaultEnableAutosave);
        setAutosaveInterval(defaultAutosaveInterval);
        setIsLoading(false);
      }
    };
    
    loadAutosaveSettings();
  }, [defaultEnableAutosave, defaultAutosaveInterval]);

  // Sauvegarder les paramètres d'autosauvegarde dans IndexedDB
  useEffect(() => {
    if (!isLoading) {
      saveToLocalStorage('enableAutosave', enableAutosave);
      saveToLocalStorage('autosaveInterval', autosaveInterval);
    }
  }, [enableAutosave, autosaveInterval, isLoading]);

  const handleExportConfiguration = useCallback(async () => {
    try {
      // Vérifier si les données ont changé
      const dataChanged = await loadFromLocalStorage('dataChanged');
      if (dataChanged === false) return;

      const filename = "autosave-dynvibe-project.json";
      const { verbatims, notes, slides, labels, labelTypes } = dataRef.current;
      const data = { verbatims, notes, slides, labels, labelTypes };
      
      // Marquer les données comme inchangées
      await saveToLocalStorage('dataChanged', false);
      
      // Créer et télécharger le fichier JSON
      const json = JSON.stringify(data);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erreur lors de l\'exportation de la configuration:', error);
    }
  }, []);

  // Configuration de l'autosauvegarde
  useEffect(() => {
    const setupAutosave = async () => {
      try {
        if (isLoading) return;
        
        let interval;
        try {
          interval = await loadFromLocalStorage('intervalNumber');
        } catch (error) {
          interval = null;
        }

        const stopAutosave = async (intervalId) => {
          if (intervalId !== null) {
            clearInterval(intervalId);
            await saveToLocalStorage('intervalNumber', null);
          }
        };

        const autoSaveFunction = async (intervalId) => {
          if (!enableAutosave) {
            await stopAutosave(intervalId);
            return null;
          } else {
            await stopAutosave(intervalId);
            let newInterval = setInterval(handleExportConfiguration, autosaveInterval * 60 * 1000);
            await saveToLocalStorage('intervalNumber', newInterval);
            return newInterval;
          }
        };

        interval = await autoSaveFunction(interval);
        return () => stopAutosave(interval);
      } catch (error) {
        console.error('Erreur lors de la configuration de l\'autosauvegarde:', error);
      }
    };
    
    setupAutosave();
  }, [enableAutosave, autosaveInterval, handleExportConfiguration, isLoading]);

  // Mettre à jour dataRef et gérer le flag dataChanged
  useEffect(() => {
    const updateDataRef = async () => {
      if (isLoading) return;
      
      // Mettre à jour dataRef avec les données actuelles
      dataRef.current = { verbatims, notes, slides, labels, labelTypes };
      
      // Vérifier et mettre à jour le flag dataChanged
      try {
        const dataChanged = await loadFromLocalStorage('dataChanged');
        if (dataChanged === false || dataChanged === null || dataChanged === undefined) {
          await saveToLocalStorage('dataChanged', true);
        }
      } catch (error) {
        console.error('Erreur lors de la mise à jour du flag dataChanged:', error);
      }
    };
    
    updateDataRef();
    
    // Réinitialiser le flag dataChanged lors du démontage
    return () => {
      saveToLocalStorage('dataChanged', false);
    };
  }, [verbatims, notes, slides, labels, labelTypes, isLoading]);

  if (isLoading) {
    return <div>Loading project settings...</div>;
  }

  return (
    <ProjectContext.Provider value={{
      verbatims, setVerbatims,
      notes, setNotes,
      slides, setSlides,
      labels, setLabels,
      labelTypes, setLabelTypes,
      enableAutosave, setEnableAutosave,
      autosaveInterval, setAutosaveInterval,
    }}>
      {children}
    </ProjectContext.Provider>
  );
};
